import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import CustomQuerySetupApiMetricBuilderRawResponseAndExpressionComponent from 'webapp/components/custom-query/setup/api-metric-builder/raw-response-and-expression';
import CustomQuerySetupCommonPreviewTableComponent from 'webapp/components/custom-query/setup/common/preview-table';
import type { Args } from 'webapp/components/custom-query/setup/wizard/api-metric-builder/step-common';
import CustomQuerySetupWizardSheetControlsComponent from 'webapp/components/custom-query/setup/wizard/sheet/controls';
import FormErrorComponent from 'webapp/components/form-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

interface CustomQuerySetupWizardSheetStepPreviewComponentInterface {
	Args: Args;
	Blocks: { default: [] }; // this is needed for yield
}

export default class CustomQuerySetupWizardApiMetricBuilderStepPreviewComponent extends Component<CustomQuerySetupWizardSheetStepPreviewComponentInterface> {
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked submitInProgress = false;

	get isPreviewButtonDisabled(): boolean {
		return !this.args.form.extractionExpression;
	}

	get isContinueButtonDisabled(): boolean {
		return (
			this.submitInProgress ||
			!this.args.form.prePreviewData ||
			this.args.form.isExpressionDirty
		);
	}

	get theme() {
		return this.navigationService.theme.id;
	}

	@action
	getPrePreview(): void {
		const form = this.args.form;

		void form.validate();

		if (!form.isValid) {
			return;
		}

		this.submitInProgress = true;
		void form.getPrePreviewData().finally(() => {
			this.submitInProgress = false;
		});
	}

	@action
	onContentChange(): void {
		// skip
	}
	<template>
		<div class="step-ct">

			<div class="top">
				<h3>Write JSONata expression to create your built metric</h3>
				<p>
					Based on your selection of properties a JSONata expression has been created. You
					can customize it in the right editor panel. To continue run the 'Preview data'
					to validate the expression and returned data.
				</p>

				{{yield}}
				{{! .toggle-manual button }}
			</div>

			<FormErrorComponent @errors={{@form.errors}} />

			<CustomQuerySetupApiMetricBuilderRawResponseAndExpressionComponent
				@form={{@form}}
				@isDisabled={{this.submitInProgress}}
				@onContentChange={{this.onContentChange}}
				@theme={{this.theme}}
			/>

			<div class="controls2">
				<div class="controls2__left">
					<SubmitButtonComponent
						@text="Preview data"
						@size="big"
						@onTrigger={{this.getPrePreview}}
						@isDisabled={{this.isPreviewButtonDisabled}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>

			{{#if @form.prePreviewData}}
				<CustomQuerySetupCommonPreviewTableComponent @form={{@form}} />
			{{/if}}

		</div>

		{{! controlsNode could still be unrendered }}
		{{#if @controlsNode}}
			{{#in-element @controlsNode}}
				<CustomQuerySetupWizardSheetControlsComponent
					@showBack={{true}}
					@isButtonDisabled={{this.isContinueButtonDisabled}}
					@goNext={{@goNext}}
					@goPrevious={{@goPrevious}}
				/>
			{{/in-element}}
		{{/if}}
	</template>
}
